import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import usePWA from '../hooks/usePWA';

/* ─────────────────────────────────────────────────────────────────────────────
   Install Guide page  (/install)
   – Native install prompt button when available
   – Manual step-by-step guide per platform
   – Redirects to /download for the full landing page
───────────────────────────────────────────────────────────────────────────── */

const STEPS = {
  android: [
    { s: '1', t: 'Open in Chrome',         d: 'Open this website in Google Chrome on your Android device.' },
    { s: '2', t: 'Tap the ⋮ menu',         d: 'Tap the three-dot menu at the top-right corner of Chrome.' },
    { s: '3', t: 'Tap "Add to Home Screen"',d: 'Select "Add to Home Screen" or "Install app" from the menu.' },
    { s: '4', t: 'Confirm & Done!',         d: 'Tap "Add". The GS Mwendo app icon will appear on your home screen.' },
  ],
  ios: [
    { s: '1', t: 'Open in Safari',          d: 'Visit this page using Safari — not Chrome or Firefox.' },
    { s: '2', t: 'Tap the Share button ⎙',  d: 'Tap the Share icon at the bottom of the Safari browser.' },
    { s: '3', t: 'Add to Home Screen',       d: 'Scroll down and tap "Add to Home Screen".' },
    { s: '4', t: 'Confirm & Done!',          d: 'Tap "Add" in the top-right corner. The app is now installed!' },
  ],
  desktop: [
    { s: '1', t: 'Open in Chrome or Edge',   d: 'Open this website in Google Chrome or Microsoft Edge.' },
    { s: '2', t: 'Look for the install icon', d: 'Find the ⊕ install icon in the right side of the address bar.' },
    { s: '3', t: 'Click Install',             d: 'Click the icon, then click "Install" in the popup dialog.' },
    { s: '4', t: 'Done!',                     d: 'The app opens in its own window and is added to your taskbar.' },
  ],
};

export default function Install() {
  const { canInstall, isInstalled, isIOS, isAndroid, triggerInstall } = usePWA();
  const [tab, setTab]           = useState(isIOS ? 'ios' : isAndroid ? 'android' : 'desktop');
  const [installing, setInstalling] = useState(false);
  const [done, setDone]         = useState(isInstalled);

  const handleInstall = async () => {
    setInstalling(true);
    const ok = await triggerInstall();
    if (ok) setDone(true);
    setInstalling(false);
  };

  const tabs = [
    { id: 'android', icon: '🤖', label: 'Android' },
    { id: 'ios',     icon: '🍎', label: 'iPhone'  },
    { id: 'desktop', icon: '💻', label: 'Desktop' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-whatsapp-darker via-whatsapp-dark to-whatsapp-green flex flex-col items-center justify-start px-4 py-8 sm:py-12">

      <div className="w-full max-w-sm sm:max-w-md">

        {/* App icon + name */}
        <div className="text-center mb-6 sm:mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-3xl shadow-2xl mb-4">
            <div className="text-center">
              <div className="text-whatsapp-darker font-black text-xl sm:text-2xl leading-none">GS</div>
              <div className="text-whatsapp-green font-black text-sm sm:text-lg leading-none">S3</div>
            </div>
          </div>
          <h1 className="text-white text-2xl sm:text-3xl font-black">Install GS Mwendo S3</h1>
          <p className="text-green-200 mt-1 text-sm">Add to your home screen in seconds</p>
          <div className="flex flex-wrap justify-center gap-2 mt-3">
            {['📱 Android','🍎 iPhone','💻 PC/Mac','📴 Offline'].map(t => (
              <span key={t} className="text-xs glass px-2.5 py-1 rounded-full text-green-200">{t}</span>
            ))}
          </div>
        </div>

        {/* ── Already installed / success ─────────────────────────────── */}
        {done ? (
          <div className="glass rounded-2xl p-5 sm:p-6 text-center mb-5 animate-fade-in">
            <div className="text-5xl mb-3">🎉</div>
            <p className="text-white font-black text-lg sm:text-xl">Installed!</p>
            <p className="text-green-200 text-sm mt-1 mb-4">GS Mwendo S3 is on your home screen.</p>
            <Link to="/chat"
              className="inline-flex items-center gap-2 bg-whatsapp-green hover:bg-green-400 text-white px-6 py-3 rounded-xl font-bold text-sm transition-colors">
              Open App →
            </Link>
          </div>
        ) : canInstall ? (
          /* ── Native install prompt available ───────────────────────── */
          <div className="glass rounded-2xl p-5 sm:p-6 mb-5 text-center animate-fade-in border border-whatsapp-green/30">
            <div className="text-4xl mb-3">📲</div>
            <h2 className="text-white font-black text-lg sm:text-xl mb-1">Install in one tap</h2>
            <p className="text-green-200 text-sm mb-5">
              Your browser supports one-tap install. No app store needed.
            </p>
            <button onClick={handleInstall} disabled={installing}
              className="w-full bg-whatsapp-green hover:bg-green-400 text-white font-black py-3.5 px-6 rounded-xl text-base transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60">
              {installing ? (
                <>
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Installing…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                  </svg>
                  Install App — Free
                </>
              )}
            </button>
            <p className="text-green-300/70 text-xs mt-3">Works offline · No app store · Free forever</p>
          </div>
        ) : null}

        {/* ── Manual guide ────────────────────────────────────────────── */}
        <div className="glass rounded-2xl overflow-hidden border border-white/10 mb-5">
          <div className="px-4 py-3 border-b border-white/10">
            <h2 className="text-white font-bold text-sm">Step-by-step guide</h2>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex-1 py-2.5 text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${
                  tab === t.id
                    ? 'bg-white/10 text-white border-b-2 border-whatsapp-green'
                    : 'text-green-300 hover:text-white'
                }`}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div className="p-4 space-y-4">
            {STEPS[tab].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-whatsapp-green flex items-center justify-center text-white text-xs font-black shadow-md">
                  {step.s}
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{step.t}</p>
                  <p className="text-green-200 text-xs mt-0.5 leading-relaxed">{step.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Benefits ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-5">
          {[
            { icon: '⚡', t: 'Fast',     d: 'Cached, instant load' },
            { icon: '📴', t: 'Offline',  d: 'No Wi-Fi needed'     },
            { icon: '🔔', t: 'Alerts',   d: 'Push notifications'  },
            { icon: '📲', t: 'Native',   d: 'Full-screen app'     },
          ].map(b => (
            <div key={b.t} className="glass rounded-xl p-3 border border-white/10">
              <div className="text-xl mb-1">{b.icon}</div>
              <p className="text-white text-xs font-bold">{b.t}</p>
              <p className="text-green-200 text-xxs">{b.d}</p>
            </div>
          ))}
        </div>

        {/* Links */}
        <div className="flex flex-col items-center gap-2">
          <Link to="/download"
            className="text-whatsapp-green hover:text-green-300 text-sm font-medium transition-colors flex items-center gap-1">
            ← See full download page
          </Link>
          <Link to="/login"
            className="text-green-300 hover:text-white text-sm transition-colors">
            Use in browser instead
          </Link>
        </div>

        <p className="text-center text-green-300/40 text-xs mt-5">
          © 2024 GS Mwendo R — Class S3
        </p>
      </div>
    </div>
  );
}
